import React, { useMemo } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Tooltip, LinearProgress,
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';
import { chartTooltipProps } from './charts/chartTooltip';
import { formatCurrency } from '../utils/currency';

export interface MixPosition {
  symbol: string;
  /** Market value in the account's currency. */
  value: number;
}

interface Props {
  positions: MixPosition[];
  /** Total account value, including cash. Positions rarely sum to it. */
  accountTotal: number;
  currency: string;
  baseCurrency: string;
}

/**
 * Distinct enough to tell apart at a glance, and readable on both themes.
 * Cash gets its own slot at the end so it never borrows a holding's colour.
 */
const SLICE_COLORS = [
  '#4f9cf9', '#5ad1a5', '#f2c14e', '#ef6f6c', '#a78bfa',
  '#3fc1c9', '#f78fb3', '#8ac926', '#ffa552', '#6c8ae4',
  '#c780e8', '#57cc99',
];
const CASH_COLOR = '#8b96a8';

/**
 * How a portfolio is split across its positions, and how much is sitting in
 * cash.
 *
 * WHY CASH IS A SLICE. Cash is derived as the account's total value minus the
 * sum of its positions, because no brokerage export lists it as a holding. A
 * pie of positions alone silently renormalises to 100%, so an account that is
 * a quarter cash shows its largest stock as a far bigger share of "the
 * portfolio" than it is of the money. Including cash is what makes the
 * percentages answer the question actually being asked.
 *
 * This states weights. It does not judge them: what counts as concentrated
 * depends on goals, horizon and everything held elsewhere, none of which are
 * in this data.
 */
export const PositionMix: React.FC<Props> = ({
  positions, accountTotal, currency, baseCurrency,
}) => {
  const { slices, cash, positionsTotal, topWeight } = useMemo(() => {
    const sorted = positions
      .filter((p) => p.value > 0)
      .sort((a, b) => b.value - a.value);
    const posTotal = sorted.reduce((s, p) => s + p.value, 0);

    // Never negative: if positions exceed the recorded balance -- stale price
    // data, a pending settlement -- reporting "-$4,000 of cash" would be
    // nonsense. Zero says "nothing left over", which is the honest reading.
    const derivedCash = Math.max(0, accountTotal - posTotal);
    const denom = posTotal + derivedCash;

    // Keep every position that is at least 1% of the account, so a genuinely
    // diversified portfolio does not collapse into one "Other" wedge -- and
    // group the long tail, which is unreadable as 40 slivers either way.
    const threshold = denom * 0.01;
    const major = sorted.filter((p) => p.value >= threshold);
    const minor = sorted.filter((p) => p.value < threshold);
    const minorTotal = minor.reduce((s, p) => s + p.value, 0);

    const out: Array<{ name: string; value: number; color: string; isCash?: boolean }> =
      major.map((p, i) => ({
        name: p.symbol,
        value: p.value,
        color: SLICE_COLORS[i % SLICE_COLORS.length],
      }));
    if (minorTotal > 0) {
      out.push({
        name: `${minor.length} smaller`,
        value: minorTotal,
        color: SLICE_COLORS[major.length % SLICE_COLORS.length],
      });
    }
    if (derivedCash > 0) {
      out.push({ name: 'Cash', value: derivedCash, color: CASH_COLOR, isCash: true });
    }

    return {
      slices: out,
      cash: derivedCash,
      positionsTotal: posTotal,
      topWeight: denom > 0 && sorted.length > 0 ? (sorted[0].value / denom) * 100 : 0,
    };
  }, [positions, accountTotal]);

  const denom = positionsTotal + cash;
  if (denom <= 0) return null;

  const money = (v: number) => formatCurrency(v, currency as any, { base: baseCurrency as any });
  const pct = (v: number) => `${((v / denom) * 100).toFixed(1)}%`;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Box sx={{ width: { xs: '100%', md: 260 }, height: 220, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                innerRadius="55%"
                outerRadius="88%"
                paddingAngle={1}
                // Without this the wedges can render at zero size and leave an
                // empty ring, which reads as "no holdings".
                isAnimationActive={false}
              >
                {slices.map((s) => (
                  <Cell key={s.name} fill={s.color} stroke="none" />
                ))}
              </Pie>
              <RTooltip
                {...chartTooltipProps}
                formatter={(v: any, n: any) => [`${money(Number(v))} · ${pct(Number(v))}`, n]}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Tooltip title="Total value of positions, excluding cash.">
              <Chip size="small" label={`Invested ${money(positionsTotal)}`} />
            </Tooltip>
            <Tooltip title="Account value less the value of its positions. Derived — no brokerage export lists cash as a holding.">
              <Chip
                size="small"
                variant="outlined"
                label={`Cash ${money(cash)} · ${pct(cash)}`}
              />
            </Tooltip>
            <Tooltip title="Share of the account, including cash, held in its single largest position.">
              <Chip
                size="small"
                variant="outlined"
                label={`Largest position ${topWeight.toFixed(1)}%`}
              />
            </Tooltip>
          </Stack>

          {/* A ranked list rather than a legend: the ordering is the point,
              and slice colours alone are hard to match back to a symbol. */}
          <Box sx={{ maxHeight: 168, overflowY: 'auto', pr: 1 }}>
            {slices.map((s) => (
              <Box key={s.name} sx={{ mb: 0.75 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.color, flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: s.isCash ? 400 : 650, minWidth: 78 }}
                  >
                    {s.name}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 40 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (s.value / denom) * 100)}
                      sx={{
                        height: 6, borderRadius: 3,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': { bgcolor: s.color, borderRadius: 3 },
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 46, textAlign: 'right' }}>
                    {pct(s.value)}
                  </Typography>
                  <Typography variant="caption" sx={{ minWidth: 92, textAlign: 'right' }}>
                    {money(s.value)}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};
